/**
 * 입력값에서 숫자만 남기고 최대 13자리로 자릅니다.
 */
export function normalizeKoreanRrnInput(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 13);
}

const RRN_CHECK_WEIGHTS = [2, 3, 4, 5, 6, 7, 8, 9, 2, 3, 4, 5];

/** 7번째 자리(성별·세기)와 앞 두 자리(YY)로 출생 연도(4자리) */
function fullBirthYearFromRrn(yymmdd: string, genderCenturyDigit: number): number | null {
  const yy = Number(yymmdd.slice(0, 2));
  if (!Number.isFinite(yy) || yy < 0 || yy > 99) return null;

  if ([1, 2, 5, 6].includes(genderCenturyDigit)) return 1900 + yy;
  if ([3, 4, 7, 8].includes(genderCenturyDigit)) return 2000 + yy;
  if ([9, 0].includes(genderCenturyDigit)) return 1800 + yy;
  return null;
}

function isValidCalendarDate(fullYear: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1) return false;
  const d = new Date(fullYear, month - 1, day);
  return d.getFullYear() === fullYear && d.getMonth() === month - 1 && d.getDate() === day;
}

/** 앞 6자리 + 7번째 자리 기준 실제 달력 날짜 */
function isRrnDateConsistent(digits: string): boolean {
  const yymmdd = digits.slice(0, 6);
  if (!/^\d{6}$/.test(yymmdd)) return false;
  const genderCenturyDigit = Number(digits[6]);
  if (!Number.isFinite(genderCenturyDigit)) return false;

  const fullYear = fullBirthYearFromRrn(yymmdd, genderCenturyDigit);
  if (fullYear === null) return false;

  const mm = Number(yymmdd.slice(2, 4));
  const dd = Number(yymmdd.slice(4, 6));
  return isValidCalendarDate(fullYear, mm, dd);
}

/** 13번째 자리 검증번호(주민등록번호/외국인등록번호 체크섬) */
function isRrnChecksumValid(digits: string): boolean {
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += Number(digits[i]) * RRN_CHECK_WEIGHTS[i];
  }
  const registrationTypeDigit = Number(digits[6]);
  const isForeigner = [5, 6, 7, 8].includes(registrationTypeDigit);

  let expected = (11 - (sum % 11)) % 10;

  // 외국인등록번호: +2 후 다시 %10
  if (isForeigner) {
    expected = (expected + 2) % 10;

    // 8·9번째 자리(행정기관 번호)는 짝수여야 함
    const agencyCode = Number(digits[7]) * 10 + Number(digits[8]);
    if (agencyCode % 2 !== 0) return false;
  }

  return expected === Number(digits[12]);
}

/**
 * 13자리 숫자, 생년월일·세기(7번째 자리) 일치,
 * 주민등록번호/외국인등록번호 체크섬을 검사합니다.
 */
export function isValidKoreanResidentRegistrationNumber(value: string): boolean {
  const digits = normalizeKoreanRrnInput(value);
  if (digits.length !== 13) return false;
  if (!/^\d{13}$/.test(digits)) return false;
  if (!isRrnDateConsistent(digits)) return false;
  return isRrnChecksumValid(digits);
}
